import axios from 'axios';
import safeStorage from './safeStorage';
import {
  AUTH_CLEARED_EVENT,
  extractBearerToken,
  isAuthExemptRequestUrl,
  shouldClearSessionOnAuthFailure,
} from './authSessionUtils';
import { createAuthResponseErrorHandler } from './handleApiAuthError';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token helpers
function getAccessToken() {
  return safeStorage.getItem('token');
}
function getRefreshToken() {
  return safeStorage.getItem('refresh_token');
}
function setAccessToken(token) {
  if (token) safeStorage.setItem('token', token);
}
function clearTokens() {
  safeStorage.removeItem('token');
  safeStorage.removeItem('refresh_token');
}

function notifyAuthCleared() {
  try {
    window.dispatchEvent(new Event(AUTH_CLEARED_EVENT));
  } catch {
    // ignore (non-browser)
  }
}

function clearSessionFromInterceptor(originalRequest) {
  const failedAccessToken = extractBearerToken(
    originalRequest?.headers?.Authorization || originalRequest?.headers?.authorization
  );
  const currentAccessToken = getAccessToken();
  if (
    !shouldClearSessionOnAuthFailure({
      failedAccessToken,
      currentAccessToken,
    })
  ) {
    return false;
  }
  clearTokens();
  stopAuthSession();
  notifyAuthCleared();
  return true;
}

// Decode JWT exp (seconds since epoch)
function getJwtExp(token) {
  try {
    const [, payload] = token.split('.');
    const json = JSON.parse(atob(payload));
    return typeof json.exp === 'number' ? json.exp : null;
  } catch {
    return null;
  }
}

// Idle tracking (30 minutes)
const IDLE_MS = 30 * 60 * 1000;
let lastActivity = Date.now();
let idleTimer = null;
let isIdle = false;

function markActive() {
  lastActivity = Date.now();
  if (isIdle) isIdle = false;
}

function setupIdleTracking() {
  const onAnyActivity = () => markActive();
  window.addEventListener('mousemove', onAnyActivity);
  window.addEventListener('keydown', onAnyActivity);
  window.addEventListener('mousedown', onAnyActivity);
  window.addEventListener('touchstart', onAnyActivity);
  window.addEventListener('visibilitychange', () => {
    if (!document.hidden) markActive();
  });
  if (idleTimer) clearInterval(idleTimer);
  idleTimer = setInterval(() => {
    const idleFor = Date.now() - lastActivity;
    isIdle = idleFor >= IDLE_MS;
  }, 30000);
}

// Single-flight refresh state
let isRefreshing = false;
let pendingRequests = [];

function subscribeTokenRefresh(cb) {
  pendingRequests.push(cb);
}
function onRefreshed(newToken) {
  pendingRequests.forEach((cb) => cb(newToken));
  pendingRequests = [];
}

async function refreshAccessToken() {
  const refresh = getRefreshToken();
  if (!refresh) throw new Error('No refresh token');
  const resp = await axios.post(`${API_BASE_URL}/token/refresh/`, { refresh });
  const { access } = resp.data || {};
  if (!access) throw new Error('No access token in refresh response');
  setAccessToken(access);
  schedulePreemptiveRefresh(access); // reschedule on successful refresh
  return access;
}

// Preemptive refresh scheduler
let preemptiveTimer = null;
const PREEMPTIVE_SAFETY_SECONDS = 60; // refresh 60s before expiry

function schedulePreemptiveRefresh(accessToken) {
  if (preemptiveTimer) {
    clearTimeout(preemptiveTimer);
    preemptiveTimer = null;
  }
  const token = accessToken || getAccessToken();
  if (!token) return;
  const exp = getJwtExp(token);
  if (!exp) return;
  const nowSec = Math.floor(Date.now() / 1000);
  const delayMs = Math.max((exp - PREEMPTIVE_SAFETY_SECONDS - nowSec) * 1000, 0);
  if (delayMs === 0) return; // already near expiry; let interceptor handle
  preemptiveTimer = setTimeout(async () => {
    // Skip preemptive refresh if user is idle
    if (isIdle) return;
    try {
      await refreshAccessToken();
    } catch {
      // Let interceptor handle eventual 401s later
    }
  }, delayMs);
}

export function startAuthSession() {
  const token = getAccessToken();
  if (token) schedulePreemptiveRefresh(token);
  setupIdleTracking();
}
export function stopAuthSession() {
  if (preemptiveTimer) {
    clearTimeout(preemptiveTimer);
    preemptiveTimer = null;
  }
  if (idleTimer) {
    clearInterval(idleTimer);
    idleTimer = null;
  }
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: 401 -> try refresh once, then retry original.
// Unrecoverable expiry clears the session and does not reject (avoids CRA overlay).
const handleAuthResponseError = createAuthResponseErrorHandler({
  getRefreshToken,
  refreshAccessToken,
  getIsRefreshing: () => isRefreshing,
  setIsRefreshing: (value) => {
    isRefreshing = value;
  },
  subscribeTokenRefresh,
  onRefreshed,
  clearSessionFromInterceptor,
  retryRequest: (originalRequest) => api(originalRequest),
  isAuthExemptRequestUrl,
});

api.interceptors.response.use(
  (response) => response,
  (error) => handleAuthResponseError(error)
);

// Kick off scheduler if already logged in
startAuthSession();

export default api;
